// src/admin/RaporYazdirma.jsx
//
// DISC kişisel raporunun baskıya hazır, Someren Labs markalı sürümü (2026-08-16).
//
// NEDEN TARAYICI BASKISI: rapor gövdesi tamamen HTML/CSS (canvas yok). Tarayıcının
// kendi "PDF olarak kaydet" çıktısı bu yüzden vektörel ve metni seçilebilir olur.
// jsPDF/html2canvas gibi kütüphaneler sayfayı görüntüye çevirir; Türkçe karakterlerde
// ve font keskinliğinde kayıp verir, ~1 MB bağımlılık ekler. Gerek yok.
//
// KULLANIM: admin ekranında "PDF / Yazdır" düğmesi bu katmanı açar, katman
// window.print() çağırır, kullanıcı yazıcı olarak "PDF olarak kaydet"i seçer.

import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import DiscPersonalReport, { KatilimciGrafikleri } from "../disc/report/DiscPersonalReport";
import { DISC_RENK } from "../disc/discBrand";
import { SinyalPanosu } from "../disc/SinyalSirasi";

export default function RaporYazdirma({
  katilimciAdi,
  sirket,
  takim,
  tarih,
  sureMetni,
  baskinHarf,
  dil = "tr",
  raporProps,
  onKapat,
  // true → katılımcının envanter sonunda gördüğü kadarı basılır:
  // kimlik künyesi + uyarlanmış/doğal çubuk grafikleri. Yorum bölümleri YOK.
  katilimciGorunumu = false,
}) {
  const tr = dil !== "en";

  // Baskı sırasında sayfanın geri kalanını gizlemek için gövdeye işaret sınıfı.
  //
  // Belge başlığı da burada değişir: tarayıcının yazdırma üstbilgisi sayfa başlığını
  // yazar ve kullanıcı bu kutuyu kapatmazsa kâğıtta "React App" görünüyordu. Aynı
  // başlık, "PDF olarak kaydet" seçildiğinde önerilen dosya adı olur — böylece dosya
  // kendiliğinden "DISC Raporu — Ad Soyad.pdf" olarak gelir.
  useEffect(() => {
    document.body.classList.add("sl-baski-aktif");
    const oncekiTaslak = document.body.style.overflow;
    const oncekiBaslik = document.title;
    document.body.style.overflow = "hidden";
    document.title = `${tr ? (katilimciGorunumu ? "DISC Sonuç Grafikleri" : "DISC Raporu") : (katilimciGorunumu ? "DISC Result Charts" : "DISC Report")} — ${katilimciAdi || "-"}`;
    return () => {
      document.body.classList.remove("sl-baski-aktif");
      document.body.style.overflow = oncekiTaslak;
      document.title = oncekiBaslik;
    };
  }, [tr, katilimciAdi, katilimciGorunumu]);

  // Esc ile kapat
  useEffect(() => {
    function tusaBasildi(e) {
      if (e.key === "Escape") onKapat?.();
    }
    window.addEventListener("keydown", tusaBasildi);
    return () => window.removeEventListener("keydown", tusaBasildi);
  }, [onKapat]);

  const harf = DISC_RENK[baskinHarf] ? baskinHarf : null;
  const vurguRenk = harf ? DISC_RENK[harf] : "#111827";

  const uretimTarihi = new Date().toLocaleString(tr ? "tr-TR" : "en-US");

  // PORTAL ŞART: katman doğrudan <body>'nin çocuğu olmalı.
  // Admin ağacının içinde kalsaydı, baskıda geri kalanı gizlemek için `visibility:hidden`
  // kullanmak gerekirdi — o da yerleşimi KORUDUĞU için gizli grafik/tablo yüksekliği
  // kâğıtta yer kaplayıp raporun ardına boş sayfalar ekliyordu. Gövde seviyesinde
  // olunca kardeşleri `display:none` ile tamamen akıştan çıkarabiliyoruz.
  return createPortal(
    <div style={st.katman} id="sl-baski-katman">
      <style>{BASKI_CSS}</style>

      {/* ---- Ekranda görünen araç çubuğu (baskıda gizli) ---- */}
      <div className="sl-ekranda" style={st.arac}>
        <div style={st.aracSol}>
          <strong style={{ fontSize: 15 }}>{katilimciAdi || "-"}</strong>
          <span style={st.aracAlt}>
            {tr ? "baskı önizleme" : "print preview"} · {tr ? "Esc ile kapat" : "press Esc to close"}
          </span>
        </div>
        <div style={st.aracSag}>
          <button style={st.dugmeBirincil} onClick={() => window.print()}>
            {tr ? "PDF olarak kaydet / Yazdır" : "Save as PDF / Print"}
          </button>
          <button style={st.dugmeIkincil} onClick={onKapat}>
            {tr ? "Kapat" : "Close"}
          </button>
        </div>
      </div>

      <p className="sl-ekranda" style={st.ipucu}>
        {tr
          ? "Yazıcı penceresinde Hedef olarak “PDF olarak kaydet” seçin. Daha temiz bir çıktı için “Üstbilgi ve altbilgi” kutusunu kapatın, “Arka plan grafikleri” kutusunu açık bırakın."
          : "In the print dialog choose “Save as PDF” as the destination. For a cleaner result turn off “Headers and footers” and keep “Background graphics” on."}
      </p>

      {/* ---- Basılan kök ----
          lang ŞART: index.html'de <html lang="en"> yazıyor ve CSS text-transform:uppercase
          büyütmeyi sayfa diline göre yapıyor. İngilizce kuralla "Şirket" → "ŞIRKET",
          "Tarih" → "TARIH" oluyor; noktalı İ kayboluyor. lang="tr" doğru büyütüyor. */}
      <div id="sl-baski-kok" lang={tr ? "tr" : "en"}>
        <div id="sl-baski-sayfa" style={st.sayfa}>
          {/* Marka başlığı */}
          <header style={st.baslikBandi} className="sl-blok">
            <div style={st.markaSol}>
              <img
                src={`${process.env.PUBLIC_URL || ""}/someren-labs-logo.svg`}
                alt="Someren Labs"
                style={st.logo}
              />
            </div>
            <div style={st.markaSag}>
              <p style={st.kicker}>{tr ? (katilimciGorunumu ? "KATILIMCI SONUÇ ÖZETİ" : "KİŞİSEL DEĞERLENDİRME RAPORU") : (katilimciGorunumu ? "PARTICIPANT RESULT SUMMARY" : "PERSONAL ASSESSMENT REPORT")}</p>
              <h1 style={st.anaBaslik}>{tr ? "DISC Davranış Profili" : "DISC Behavioural Profile"}</h1>
            </div>
          </header>

          <div style={{ ...st.vurguCizgi, background: vurguRenk }} />

          {/* Kimlik künyesi */}
          <section style={st.kunye} className="sl-blok">
            <div style={st.kunyeIsim}>
              <span style={st.kunyeEtiket}>{tr ? "Katılımcı" : "Participant"}</span>
              <strong style={st.kunyeDeger}>{katilimciAdi || "-"}</strong>
            </div>

            {/* Katılımcı görünümünde de gösterilir: katılımcı aynı bilgiyi
                envanter sonu ekranında da görüyor, ikisi birebir aynı kalmalı. */}
            <SinyalPanosu
              adapted={raporProps?.adaptedScores}
              natural={raporProps?.naturalScores}
              tr={tr}
            />

            <div style={st.kunyeIzgara} id="sl-baski-kunye">
              <Kunye etiket={tr ? "Şirket / Kurum" : "Company"} deger={sirket} />
              <Kunye etiket={tr ? "Takım" : "Team"} deger={takim} />
              <Kunye etiket={tr ? "Uygulama tarihi" : "Completion date"} deger={tarih} />
              <Kunye etiket={tr ? "Tamamlama süresi" : "Duration"} deger={sureMetni} />
            </div>
          </section>

          {/* Gövde. Katılımcı görünümünde yalnız iki çubuk grafiği basılır;
              tam raporda ise bileşenin tamamı. baskiModu, bileşenin kendi koyu
              başlık kutusunu ve künye ızgarasını atlatır; o bilgiler antette var. */}
          {katilimciGorunumu ? (
            <KatilimciGrafikleri
              adapted={raporProps?.adaptedScores}
              natural={raporProps?.naturalScores}
              language={dil}
            />
          ) : (
            <DiscPersonalReport {...raporProps} baskiModu />
          )}

          {/* Alt bilgi */}
          <footer style={st.altBilgi} className="sl-blok">
            <div>
              <strong>© 2026 Someren Labs</strong> — {tr ? "Tüm hakları saklıdır." : "All rights reserved."}
            </div>
            <div style={st.altBilgiSag}>
              {tr ? "Rapor üretim tarihi" : "Report generated"}: {uretimTarihi}
            </div>
          </footer>
        </div>
      </div>
    </div>,
    document.body
  );
}


function Kunye({ etiket, deger }) {
  return (
    <div style={st.kunyeHucre}>
      <span style={st.kunyeEtiket}>{etiket}</span>
      <strong style={st.kunyeKucukDeger}>{deger || "-"}</strong>
    </div>
  );
}

// ── Baskı kuralları ─────────────────────────────────────────────────────────
// Katman portal ile <body> altına basıldığı için, baskıda gövdenin diğer TÜM
// çocuklarını `display:none` ile akıştan çıkarabiliyoruz. `visibility:hidden`
// kullanılsaydı gizlenen admin ekranı yüksekliğini korur ve raporun ardına
// boş sayfalar eklenirdi.
const BASKI_CSS = `
@page { size: A4; margin: 10mm 12mm 12mm; }

@media print {
  body.sl-baski-aktif > *:not(#sl-baski-katman) { display: none !important; }
  body.sl-baski-aktif { overflow: visible !important; height: auto !important; }

  body.sl-baski-aktif #sl-baski-katman {
    position: static !important;
    background: #fff !important;
    padding: 0 !important;
    margin: 0 !important;
    overflow: visible !important;
    height: auto !important;
  }
  #sl-baski-sayfa {
    width: auto !important;
    max-width: none !important;
    margin: 0 !important;
    padding: 0 !important;
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }

  /* Renkli bantlar, DISC rozetleri ve puan çubukları baskıda da kalsın */
  #sl-baski-kok {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* Kişiselleştirilmiş rapor sekiz bölümden oluşur. Antet ve künye temiz bir
     kapak olarak kalır; her rapor bölümü yeni A4 sayfada başlar. */
  #sl-baski-kok .sl-report-page {
    break-before: page;
    page-break-before: always;
    break-inside: auto !important;
    page-break-inside: auto !important;
    min-height: 0 !important;
  }

  /* IZGARA → NORMAL AKIŞ.
     Chrome, CSS Grid kaplarını sayfalara bölerken ızgara elemanlarının
     break-inside kuralını UYGULAMIYOR: kartlar sayfa ortasından kesiliyordu
     (ör. "Güçlü Davranış Sinyalleri" başlığı bir sayfada, maddeleri diğerinde).
     Ayrıca ızgara satırları eşit yüksekliğe gerildiği için kısa kartın altında
     kocaman boşluk kalıyordu. Baskıda inline-block'a çeviriyoruz: iki sütunlu
     görünüm korunur, ama kartlar normal akışta yer aldığı için bölünme kuralı
     çalışır ve gerilme kaynaklı boşluklar kaybolur. */
  #sl-baski-kok .sl-izgara { display: block !important; }
  #sl-baski-kok .sl-izgara > article {
    display: inline-block !important;
    width: 49.3% !important;
    vertical-align: top;
    box-sizing: border-box;
    margin: 0 0 11px !important;
  }
  #sl-baski-kok .sl-izgara > article:nth-child(odd) { margin-right: 1.2% !important; }

  /* Özet grubu ÜÇ karttan oluşuyor: iki sütunda üçüncü kart tek başına satıra
     düşüyor ve sağında sayfanın üçte biri kadar boşluk kalıyordu. Baskıda tam
     genişlikte alt alta diziliyorlar — Executive Snapshot zaten raporun tepe
     özeti, tam genişlik ona yakışıyor. */
  #sl-baski-kok .sl-ozet > article {
    display: block !important;
    width: 100% !important;
    margin-right: 0 !important;
  }

  /* Kutular sayfa arasında ortadan bölünmesin.
     NOT: inline-block'a çevrilen kartlara break-inside VERİLMEZ — atomik satır içi
     kutu oldukları için zaten bölünemezler, ama kural eklenince Chrome satırları
     olduğundan daha isteksiz yerleştiriyor ve sayfa dibinde büyük boşluk kalıyor. */
  #sl-baski-kok article,
  #sl-baski-kok .sl-blok { break-inside: avoid; page-break-inside: avoid; }
  #sl-baski-kok .sl-report-page { break-inside: auto !important; page-break-inside: auto !important; }
  /* NOT: inline-block'a çevrilen kartlar zaten atomik satır-içi kutudur; bir kart
     satırı sayfaya sığmazsa Chrome satırın tamamını sonraki sayfaya taşır, ortadan
     kesmez. Ölçüldü: tek sütuna almak 5 sayfa / ort. %72 doluluk veriyor, iki sütun
     4 sayfa / ort. %79. İki sütun korundu. */

  /* Künye: baskıda dört alan tek satırda kalsın (auto-fit 3+1 kırıyordu) */
  #sl-baski-kunye { grid-template-columns: repeat(4, 1fr) !important; }

  /* Madde ortadan ikiye bölünmesin; başlık sayfanın dibinde yalnız kalmasın */
  #sl-baski-kok li { break-inside: avoid; page-break-inside: avoid; }
  #sl-baski-kok h1,
  #sl-baski-kok h2,
  #sl-baski-kok h3 { break-after: avoid; page-break-after: avoid; }
  #sl-baski-kok p { orphans: 3; widows: 3; }

  .sl-ekranda { display: none !important; }
}
`;

const st = {
  katman: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: "#eef1f5",
    overflowY: "auto",
    padding: "0 0 48px",
    fontFamily:
      "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  arac: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    padding: "12px 20px",
    background: "#111827",
    color: "#f9fafb",
    boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
  },
  aracSol: { display: "flex", flexDirection: "column", lineHeight: 1.3 },
  aracAlt: { fontSize: 12, opacity: 0.65 },
  aracSag: { display: "flex", gap: 8 },
  dugmeBirincil: {
    border: 0,
    borderRadius: 10,
    background: "#f59e0b",
    color: "#111827",
    fontWeight: 800,
    fontSize: 14,
    padding: "10px 16px",
    cursor: "pointer",
  },
  dugmeIkincil: {
    border: "1px solid #4b5563",
    borderRadius: 10,
    background: "transparent",
    color: "#e5e7eb",
    fontSize: 14,
    padding: "10px 16px",
    cursor: "pointer",
  },
  ipucu: {
    maxWidth: 820,
    margin: "14px auto 0",
    padding: "0 20px",
    fontSize: 12.5,
    lineHeight: 1.6,
    color: "#4b5563",
    textAlign: "center",
  },
  sayfa: {
    width: "210mm",
    maxWidth: "calc(100% - 24px)",
    margin: "18px auto 0",
    padding: "18mm 16mm",
    background: "#ffffff",
    borderRadius: 6,
    boxShadow: "0 8px 30px rgba(15,23,42,0.14)",
    boxSizing: "border-box",
    color: "#111827",
  },
  baslikBandi: {
    display: "flex",
    alignItems: "center",
    gap: 20,
    paddingBottom: 14,
  },
  markaSol: { flex: "0 0 auto" },
  logo: { width: 132, height: "auto", display: "block" },
  markaSag: { flex: "1 1 auto", textAlign: "right" },
  kicker: {
    margin: 0,
    fontSize: 10.5,
    letterSpacing: "0.18em",
    fontWeight: 800,
    color: "#6b7280",
  },
  anaBaslik: { margin: "6px 0 0", fontSize: 25, lineHeight: 1.15, color: "#08060d" },
  vurguCizgi: { height: 4, borderRadius: 2, marginBottom: 18 },
  kunye: {
    display: "grid",
    gap: 14,
    padding: "16px 18px",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    background: "#fafbfc",
  },
  kunyeIsim: { display: "flex", flexDirection: "column", gap: 3 },
  kunyeDeger: { fontSize: 22, color: "#08060d", lineHeight: 1.2 },
  kunyeEtiket: {
    fontSize: 10.5,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    fontWeight: 700,
    color: "#6b7280",
  },
  kunyeKucukDeger: { fontSize: 14, color: "#111827" },
  kunyeIzgara: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 12,
    borderTop: "1px solid #eceff3",
    paddingTop: 12,
  },
  kunyeHucre: { display: "flex", flexDirection: "column", gap: 3 },
  rozetKutu: { display: "flex", alignItems: "center", gap: 10 },
  rozet: {
    width: 38,
    height: 38,
    borderRadius: 10,
    color: "#fff",
    fontWeight: 900,
    fontSize: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
  },
  rozetMetin: { fontSize: 12.5, lineHeight: 1.4, color: "#4b5563" },
  altBilgi: {
    marginTop: 26,
    paddingTop: 12,
    borderTop: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    fontSize: 11.5,
    color: "#6b7280",
  },
  altBilgiSag: { textAlign: "right" },
};
